/**
 * Kişisel not offline kuyruğu (83. oturum politikası)
 *
 * - Yalnızca KİŞİSEL notlar kuyruğa girer. Ortak çalışma notları (isCollaborative)
 *   çevrimdışıyken salt okunur olduğu için buraya asla yazılmaz.
 * - Anahtar: notesplus_offline_notes_{userId}
 * - Legacy `notesplus_pending_queue_{userId}` ile KARIŞTIRILMAZ; o veriye dokunulmaz.
 * - Kayıtlar noteId başına birleştirilir (son yazım kazanır) — kişisel notta LWW yeterli.
 */

const QUEUE_PREFIX = 'notesplus_offline_notes_';
const QUEUE_VERSION = 1;

export const getOfflineQueueKey = (userId) => `${QUEUE_PREFIX}${userId}`;

/** PostgreSQL uyumlu UUID v4 — sunucu geçerli UUID'yi olduğu gibi kullanır (idempotent upsert) */
export const generateNoteId = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

export const loadOfflineQueue = (userId) => {
  if (!userId || typeof window === 'undefined') return [];
  try {
    const stored = window.localStorage.getItem(getOfflineQueueKey(userId));
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!parsed || !Array.isArray(parsed.ops)) return [];
    return parsed.ops.filter((op) => op && typeof op.noteId === 'string');
  } catch {
    return [];
  }
};

const saveOfflineQueue = (userId, ops) => {
  if (!userId || typeof window === 'undefined') return;
  try {
    if (!ops.length) {
      window.localStorage.removeItem(getOfflineQueueKey(userId));
      return;
    }
    window.localStorage.setItem(
      getOfflineQueueKey(userId),
      JSON.stringify({ version: QUEUE_VERSION, ops })
    );
  } catch {
    /* kota/private mode — sessiz geç */
  }
};

/**
 * Kuyruğa kişisel not operasyonu ekler / mevcut kaydı günceller.
 * `create` tipi korunur: offline oluşturulan not sonradan güncellenirse hâlâ create'tir.
 */
export const enqueueOfflineNoteOp = (userId, op) => {
  if (!userId || !op?.noteId) return [];
  const ops = loadOfflineQueue(userId);
  const index = ops.findIndex((item) => item.noteId === op.noteId);
  const now = new Date().toISOString();

  if (index === -1) {
    ops.push({
      noteId: op.noteId,
      type: op.type === 'create' ? 'create' : 'update',
      title: op.title || '',
      content: op.content || '',
      titleColor: op.titleColor || '#ffffff',
      categories: Array.isArray(op.categories) ? op.categories : undefined,
      createdAt: op.createdAt || now,
      updatedAt: op.updatedAt || now,
      queuedAt: now,
      attempts: 0,
      lastError: null,
    });
  } else {
    const existing = ops[index];
    ops[index] = {
      ...existing,
      type: existing.type === 'create' ? 'create' : (op.type === 'create' ? 'create' : 'update'),
      title: op.title !== undefined ? op.title : existing.title,
      content: op.content !== undefined ? op.content : existing.content,
      titleColor: op.titleColor || existing.titleColor,
      categories: op.categories !== undefined ? op.categories : existing.categories,
      createdAt: existing.createdAt || op.createdAt || now,
      updatedAt: op.updatedAt || now,
      queuedAt: now,
      lastError: null,
    };
  }

  saveOfflineQueue(userId, ops);
  return ops;
};

export const removeOfflineNoteOp = (userId, noteId) => {
  if (!userId || !noteId) return [];
  const ops = loadOfflineQueue(userId).filter((op) => op.noteId !== noteId);
  saveOfflineQueue(userId, ops);
  return ops;
};

export const markOfflineNoteOpFailure = (userId, noteId, message) => {
  if (!userId || !noteId) return;
  const ops = loadOfflineQueue(userId);
  const index = ops.findIndex((op) => op.noteId === noteId);
  if (index === -1) return;
  ops[index] = {
    ...ops[index],
    attempts: (ops[index].attempts || 0) + 1,
    lastError: message ? String(message).slice(0, 300) : 'UNKNOWN_ERROR',
  };
  saveOfflineQueue(userId, ops);
};

export const getOfflineQueueCount = (userId) => loadOfflineQueue(userId).length;

const isSharedNoteRecord = (note) => !!(note?.isCollaborative || note?.myRole === 'editor');

/** Queue kaydını UI notuna çevir (offline-created not cold-start hydrate) */
export const noteFromOfflineOp = (op, unnamedTitle = '') => ({
  id: op.noteId,
  title: op.title || unnamedTitle,
  content: op.content || '',
  titleColor: op.titleColor || '#ffffff',
  createdAt: op.createdAt,
  updatedAt: op.updatedAt,
  isCollaborative: false,
  myRole: null,
  collaborators: [],
  categories: Array.isArray(op.categories) ? op.categories : [],
});

/**
 * Bekleyen kişisel create/update kayıtlarını not listesinin üzerine uygula.
 * Pending local mutation henüz sunucuda olmadığı için server snapshot ezemez
 * (updatedAt karşılaştırması yok — kuyruk kaydı kazanır).
 * Ortak notlara dokunulmaz; belirsiz/shared kayıt hydrate edilmez.
 */
export const applyOfflineQueueOverlay = (notesList = [], ops = []) => {
  if (!Array.isArray(ops) || ops.length === 0) {
    return Array.isArray(notesList) ? [...notesList] : [];
  }

  const map = new Map();
  (Array.isArray(notesList) ? notesList : []).forEach((note) => {
    if (note?.id != null) map.set(String(note.id), note);
  });

  const appended = [];

  ops.forEach((op) => {
    if (!op?.noteId) return;
    const key = String(op.noteId);
    const existing = map.get(key);

    // Ortak çalışma notuna kuyruk overlay uygulanmaz
    if (existing && isSharedNoteRecord(existing)) return;

    if (existing) {
      map.set(key, {
        ...existing,
        title: op.title !== undefined ? op.title : existing.title,
        content: op.content !== undefined ? op.content : existing.content,
        titleColor: op.titleColor || existing.titleColor || '#ffffff',
        categories: op.categories !== undefined
          ? (Array.isArray(op.categories) ? op.categories : existing.categories)
          : existing.categories,
        createdAt: existing.createdAt || op.createdAt,
        updatedAt: op.updatedAt || existing.updatedAt,
      });
      return;
    }

    // Offline create (veya listede olmayan update) — kişisel not olarak ekle
    const created = noteFromOfflineOp(op);
    map.set(key, created);
    appended.push(created);
  });

  const result = [];
  const seen = new Set();
  (Array.isArray(notesList) ? notesList : []).forEach((note) => {
    const next = map.get(String(note.id));
    if (!next || seen.has(String(next.id))) return;
    result.push(next);
    seen.add(String(next.id));
  });
  appended.forEach((note) => {
    if (seen.has(String(note.id))) return;
    result.push(note);
    seen.add(String(note.id));
  });
  return result;
};

/**
 * Herhangi bir kullanıcı için bekleyen kayıt var mı?
 * Reload kapısı auth state'ine bağımlı olmasın diye doğrudan localStorage taranır.
 */
export const hasAnyOfflineQueue = () => {
  if (typeof window === 'undefined') return false;
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(QUEUE_PREFIX)) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed?.ops) && parsed.ops.length > 0) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
};
